import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { SelectItem } from '@openng/optimus-ui/api';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { Competency } from 'src/modules/enaio-certificates/shared/competency.model';
import { Pupil } from 'src/modules/enaio-certificates/shared/pupil.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { ObjectOfSimpleDataTypes } from 'src/modules/utils/shared/object-of-simple-data-types.model';
import { Objects } from "src/modules/utils/shared/objects";
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from 'src/modules/utils/shared/utils';
import { EnaioCertificateService } from '../../services/enaio-certificate.service';
import { CertificateGrade } from '../../shared/certificate-grade.entity';
import { Certificate } from '../../shared/certificate.entity';
import { CourseParticipantDto } from '../../shared/course-participant.dto';
import { EnaioCertificatesTools } from '../../shared/enaio-certificates-tools';
import { GlobalSettings } from '../../shared/global-settings.entity';
import { GradeBookSchoolClass } from '../../shared/grade-book-school-class.entity';
import { GradeType } from '../../shared/grade-type.enum';
import { GuiCertificate } from '../../shared/gui-certificate.model';
import { GuiMarkAssignment } from '../../shared/gui-mark-assignment.model';
import { SchoolClass } from '../../shared/school-class.model';
import { SchoolTypeSettings } from '../../shared/school-type-settings.entity';
import { SchoolType } from '../../shared/school-type.enum';
import { Teacher } from '../../shared/teacher.model';
import { ViewInfo } from 'src/modules/sm-base/models/view-info.model';
import { ViewState } from 'src/modules/sm-base/models/view-state.model';

export class GuiDomainGrade {
    constructor(public domain: string, public label: string, public group: string, public average: number, public o: CertificateGrade, public firstOfGroup = false) {
        this.average = this.average == null ? null : Utils.toNumber(Utils.restrictDecimalDigits(this.average, 2));
    }
}

@Component({
    selector: 'app-edit-certificate',
    templateUrl: './edit-certificate.component.html',
    styleUrls: ['./edit-certificate.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditCertificateComponent extends ComponentView {

    _SchoolType = SchoolType;
    _GradeType = GradeType;

    service = inject(EnaioCertificateService);

    neededParams: ObjectOfSimpleDataTypes = { schoolClassId: "number", isHalfYear: "bool", pupilId: "number" };
    schoolClassId = 0;
    isHalfYear = false;
    pupilId = 0;

    isAdmin = false;
    loaded = false;
    nothingFound = false;
    schoolClass: SchoolClass;
    pupil: Pupil;
    editingAllowed: boolean = null;
    globalSettings: GlobalSettings;
    settings: SchoolTypeSettings;
    certificate: Certificate;
    isGradeCertificate = false;
    competencies: Competency[] = [];
    certificateGradeItems: FrontendFieldListItem[];
    gradeBookSchoolClass: GradeBookSchoolClass;
    courses: CourseParticipantDto[];
    grades: GuiDomainGrade[];
    guiCertificate: GuiCertificate;
    originalGuiCertificate: GuiCertificate;
    teachers: Teacher[] = [];
    domains: SelectItem[] = [];
    selectedDomain = "";
    markNames: string[];
    addTeacherNameForDomains: string[] = [];
    lastYearCertId = 0;
    isForFinalExam = false;

    keepSelectedDomain = false;

    getInitialViewState(): ViewState {
        return new ViewState(true);
    }

    async initParams(): Promise<boolean> {
        await this.init();
        return true;
    }

    async init(updateSelectedDomain = true): Promise<void> {
        if (this.keepSelectedDomain) {
            this.keepSelectedDomain = false;
            updateSelectedDomain = false;
        }

        this.isAdmin = this.service.isAdminForCertificates();
        this.loaded = false;
        this.nothingFound = false;

        let restResult = await Promise.all([
            RestEndpoint.main().find({ schoolClassId: this.schoolClassId, pupilId: this.pupilId, isHalfYear: this.isHalfYear }).run("api/cer/certificate").get(Certificate),
            this.service.restGetPupil(this.pupilId),
            this.service.restGetSchoolClass(this.schoolClassId),
            RestEndpoint.main().find({}).run("api/cer/globalsettings").get(GlobalSettings),
            this.service.restGetTeachers()
        ]);

        this.pupil = restResult[1];

        if (this.pupil == null) {
            this.viewState.setViewInfo(new ViewInfo("Fehler", "Es wurde ein nicht vorhandener Schüler angefordert", "fas fa-circle-xmark"));
            return;
        }

        this.schoolClass = restResult[2];
        this.globalSettings = restResult[3];
        this.editingAllowed = this.globalSettings.editingAllowed;
        this.teachers = restResult[4];
        this.app.updateNavigation("Zeugnis bearbeiten für " + this.pupil.getFullName(), [
            this.service.getHomeCertificates(),
            { label: this.pupil.grade + this.pupil.title, routerLink: ["/enaio-certificates/pupils", this.schoolClassId, this.isHalfYear] },
            { label: this.pupil.getFullName(), routerLink: ["/enaio-certificates/certificate", this.schoolClassId, this.isHalfYear, this.pupilId] }
        ]);

        this.settings = await RestEndpoint.main().find({schoolType: this.schoolClass.getSchoolType(), year: this.schoolClass.getYearNumber(), isHalfYear: this.isHalfYear}).run("api/cer/schooltypesettings").get(SchoolTypeSettings);

        this.isForFinalExam = this.schoolClass.isForFinalExams() &&this.pupil.isForFinalExam();

        let hiddenCompetencies: number[] = [];
        if (this.settings.canCopyLastYear(this.schoolClass.grade)) {
            this.lastYearCertId = await RestEndpoint.main().query({ pupilId: this.pupil.id, schoolClassId: this.schoolClass.id, isHalfYear: this.isHalfYear}).run("api/cer/certificate/canCopyLastYear").getNumber();
            if (restResult[0] == null && this.lastYearCertId > 0 &&
                await this.app.dialog.yesNo("Möchten Sie die ausgewählten Kompetenzen für dieses Zeugnis aus dem vorherigen Schuljahr übernehmen?", "Übernahme")) {
                hiddenCompetencies = await this.loadHiddenCompetencies();
            }
        }

        this.certificate = restResult[0] || new Certificate();
        this.certificate.customInitializer();
        this.certificate.schoolClassId = this.schoolClassId;
        this.certificate.pupilId = this.pupilId;
        this.certificate.isHalfYear = this.isHalfYear;
        this.isGradeCertificate = this.schoolClass.usesGradeCertificates();

        this.competencies = restResult[0] == null || this.isGradeCertificate ?
            await RestEndpoint.main().find({ schoolClassId: this.schoolClassId, pupilId: this.pupilId }).run("api/cer/competency").list(Competency) : null;

        if (this.isGradeCertificate || this.pupil.secondForeignLanguage == "WPU") {
            this.courses = (await RestEndpoint.main().query({year: this.schoolClass.getYearNumber(), isHalfYear: this.isHalfYear, pupilIds: this.pupilId}).run("api/cer/classregister/coursesforpupils").list(CourseParticipantDto)).filter(c => c.forCertificate);
        }

        this.calculate(hiddenCompetencies);

        if (this.isGradeCertificate) {
            this.certificateGradeItems = ["", "1", "2", "3", "4", "5", "6", "erteilt", "nicht beurteilbar"].map(gi => new FrontendFieldListItem(gi, gi));
            this.gradeBookSchoolClass = await RestEndpoint.main().find({ schoolClassId: this.schoolClassId, isHalfYear: this.isHalfYear }).run("api/cer/gradebookschoolclass").get(GradeBookSchoolClass);
            let averageGrades = this.gradeBookSchoolClass.getPupilAverageMap(this.globalSettings, this.settings, this.schoolClass, null, [this.pupil]);
            let domainGroups = EnaioCertificatesTools.getDomainGroupsForMarkCertificates();

            let domains = Arrays.sort(Arrays.getUnique(this.competencies.filter(c => !c.isConductGrade()).map(c => c.domain)));
            let plainDomains = Arrays.getUnique([
                "Fleiß",
                "Zuverlässigkeit",
                "Umgangsformen",
                "Teamfähigkeit",
                ...domains,
                ...this.certificate.certificateGrades.filter(course => this.courses.some(c => c.courseId == course.id)).map(cg => cg.domain),
                ...this.courses.map(course => "course-" + course.courseId)]);
            if (this.courses.length > 0 && !plainDomains.includes("course-total")) {
                plainDomains.push("course-total");
            }
            if (this.courses.length == 0 && plainDomains.includes("course-total")) {
                plainDomains = Arrays.without(plainDomains, "course-total");
            }

            plainDomains = Arrays.without(plainDomains, "Kompetenzentwicklung");
            //Kleiner HACK. Muss noch rausfinden wieso diese beiden nicht automatisch reinkommen
            if (!plainDomains.includes("Sport")) {
                plainDomains.push("Sport");
            }
            if (!plainDomains.includes("Musik")) {
                plainDomains.push("Musik");
            }
            let d = Arrays.sort(plainDomains.map(domain => ({ domain, group: EnaioCertificatesTools.getDomainGroupForMarkCertificate(domain)})),
                (domain1, domain2) => Objects.cmpMulti([domainGroups.indexOf(domain1.group), domainGroups.indexOf(domain2.group), domain1.domain, domain2.domain]));

            this.grades = d.map(domain => new GuiDomainGrade(domain.domain,
                domain.domain == "course-total" ? "Wahlpflichtunterricht Gesamt" : domain.domain.startsWith("course-") ? this.courses.find(course => course.courseId == Utils.toNumber(Strings.removePrefix(domain.domain, "course-")))?.courseNameForCertificate ?? domain.domain
                              : domain.domain,
                domain.group, Objects.getPropertyDef(averageGrades, this.pupil.id + "|" + domain.domain, null) as number,
                this.certificate.certificateGrades.find(g => g.pupilId == this.pupil.id && g.domain == domain.domain) ?? new CertificateGrade(this.pupil.id, domain.domain)));

            for (let i = 0; i < this.grades.length; i++) {
                if (i == 0 || this.grades[i - 1].group != this.grades[i].group) {
                    this.grades[i].firstOfGroup = true;
                }
            }
        }
        else {
            this.gradeBookSchoolClass = null;
        }

        if (this.guiCertificate.domains.size() > 0) {
            if (updateSelectedDomain || !this.guiCertificate.domains.has(this.selectedDomain)) {
                this.selectedDomain = this.domains[0].value;
            }
            this.selectedDomainChanged();
            this.loaded = true;
        }
        else {
            this.nothingFound = true;
        }
        this.viewState.loading = false;
    }

    override getCanDeactivateMessage(): string {
        return this.isChanged() ? "" : null;
    }

    isChanged(): boolean {
        const updated = this.getUpdateCertificate();
        return updated.marks.length > 0 || updated.domains.length > 0;
    }

    getThisTeacherName(): string {
        let teacherName = Arrays.findAndConvert(this.teachers, t => t.userName.toLowerCase() == this.app.getUserName().toLowerCase(), t => t.fullName, this.app.getUserName());
        let parts = Strings.split(teacherName, " ");
        return parts[0].substring(0, 1) + ". " + parts[parts.length - 1];
    }

    getUpdateCertificate(forceFull = false): Certificate {
        let result = Certificate.fromGui(this.guiCertificate);
        result.certificateGrades = this.grades != null ? this.grades.map(g => g.o) : [];
        for (let domain of this.addTeacherNameForDomains) {
            result.domains.find(d => d.domain == domain).teacher = this.getThisTeacherName();
        }
        if (result.id === 0 || forceFull) {
            return result;
        }

        let oldCert = Certificate.fromGui(this.originalGuiCertificate);
        result.marks = Arrays.getOnlyUpdatedEntries(result.marks, oldCert.marks, (item1, item2) => item1.id === item2.id, (item1, item2) => item2.wasUpdated(item1));
        result.domains = Arrays.getOnlyUpdatedEntries(result.domains, oldCert.domains, (item1, item2) => item1.id === item2.id, (item1, item2) => item2.wasUpdated(item1));

        return result;
    }

    selectedDomainChanged(): void {
        this.markNames = EnaioCertificatesTools.getMarkNames(this.schoolClass.getSchoolType(), this.schoolClass.grade, this.selectedDomain, this.settings, true).slice(1);
    }

    calculate(hiddenCompetencies: number[]): void {
        this.guiCertificate = this.certificate.toGui(this.competencies, hiddenCompetencies, this.pupil.secondForeignLanguage, this.courses);
        this.originalGuiCertificate = this.certificate.toGui(this.competencies);
        let domainNames = Arrays.fromIt(this.guiCertificate.domains.values()).filter(domain => domain.hasAnyValidMarks(this.pupil)).sort((a, b) => Objects.cmp(a.getSortPriority(), b.getSortPriority())).map(s => s.domain);
        this.domains = domainNames.map(s => ({ label: s, value: s }));
    }

    async preview(): Promise<void> {
        /*
        Nicht mehr notwendig?
        if (await this.app.dialog.show("Das Speichern oder Drucken dieses Dokumentes ist strengstens untersagt!", "Warnung", [this.app.dialog.cancel, this.app.dialog.confirm]) !== "confirm") {
            return;
        }*/

        let id = await RestEndpoint.main().post().body(this.getUpdateCertificate(true)).run("api/cer/certificate/generatedocumentfordata").get(Certificate);
        window.open(RestEndpoint.main().query({id: id.id}).action("api/cer/certificate/generateddocument").generateUrl());
    }

    async saveDocument(): Promise<void> {
        if (await this.app.dialog.yesNo("Soll das Zeugnis als Dokument in enaio abgelegt werden?", "Bestätigung")) {
            this.viewState.loading = true;
            await this.save();
            await RestEndpoint.main().query({ id: this.guiCertificate.id, forHalfYear: EnaioCertificatesTools.getCertificateIsForHalfYear(new Date()) }).run("api/cer/certificate/savedocument").getText();
            this.viewState.loading = false;
        }
    }

    async saveClick(plusPupilIndex: number): Promise<void> {
        this.viewState.loading = true;
        await this.save(plusPupilIndex);
        this.viewState.loading = false;
    }

    async refresh(): Promise<void> {
        if (this.isChanged()) {
            await this.app.dialog.info("Bitte speichern Sie zuerst vorgenommene Änderungen");
            return;
        }

        this.viewState.loading = true;
        let changes = await RestEndpoint.main().query({id: this.certificate.id, previewOnly: true}).run("api/cer/certificate/refresh").listStrings();
        if (changes.length === 0) {
            this.viewState.loading = false;
            await this.app.dialog.info("Es wurden keine Änderungen in den zugrundeliegenden Kompetenzen festgestellt. Das Zeugnis bleibt unverändert");
        }
        else {
            this.viewState.loading = false;
            if (await this.app.dialog.yesNo("Folgende Änderungen werden vorgenommen. Fortfahren?<br><br>" + Arrays.itemsToString(changes, "<br>"), "Bestätigung")) {
                this.viewState.loading = true;
                await RestEndpoint.main().query({id: this.certificate.id, previewOnly: false}).run("api/cer/certificate/refresh").getText();
                await this.init(false);
                this.viewState.loading = false;
            }
        }

        this.viewState.loading = false;
    }

    async save(plusPupilIndex = 0): Promise<any> {
        await RestEndpoint.main().upsert(this.getUpdateCertificate()).run("api/cer/certificate").getText();

        if (plusPupilIndex != 0) {
            let pupils = await this.service.restGetPupils(this.schoolClass.id);
            let index = pupils.findIndex(p => p.id == this.pupil.id);
            if (index == -1) {
                await this.app.dialog.info("Konnte nicht automatisch zu nächstem Zeugnis springen");
            }
            else {
                let p = pupils[(index + plusPupilIndex + pupils.length) % pupils.length];
                this.keepSelectedDomain = true;
                this.skipCloseCheck = true;
                await this.app.navigateTo(["/enaio-certificates", "certificate", this.schoolClass.id, this.isHalfYear ? 1 : 0, p.id]);
            }
        }
        else {
            await this.init(false);
            this.originalGuiCertificate = Objects.cloneDeep(this.guiCertificate);
        }
    }

    markVisibilityChanged(event: any, mark: GuiMarkAssignment): void {
        mark.hidden = !event.checked;
    }

    allMarksVisibilityChanged(event: any): void {
        for (let mark of this.guiCertificate.domains.get(this.selectedDomain).marksFlat) {
            mark.hidden = !event.checked;
        }
    }

    getAllMarksVisible(): boolean {
        let hiddenValues = Arrays.getUnique(this.guiCertificate.domains.get(this.selectedDomain).marksFlat.map(mark => mark.hidden));
        return hiddenValues.includes(true) ? hiddenValues.includes(false) ? null : false : true;
    }

    level2VisibilityChanged(event: any, mark: GuiMarkAssignment): void {
        let v = event.checked ?? false;
        for (let m of this.guiCertificate.domains.get(this.selectedDomain).marksFlat) {
            if (m.level1 == mark.level1 && m.level2 == mark.level2) {
                m.hidden = !v;
            }
        }
    }

    level1VisibilityChanged(event: any, mark: GuiMarkAssignment): void {
        let v = event.checked ?? false;
        for (let m of this.guiCertificate.domains.get(this.selectedDomain).marksFlat) {
            if (m.level1 == mark.level1) {
                m.hidden = !v;
            }
        }
    }

    getLevel1Hidden(mark: GuiMarkAssignment): boolean {
        let unique = Arrays.getUnique(this.guiCertificate.domains.get(this.selectedDomain).marksFlat.filter(m => this.guiCertificate.domains.get(this.selectedDomain) && m.level1 == mark.level1).map(m => m.hidden));
        return unique.includes(true) ? unique.includes(false) ? null : false : true;
    }

    getLevel2Hidden(mark: GuiMarkAssignment): boolean {
        let unique = Arrays.getUnique(this.guiCertificate.domains.get(this.selectedDomain).marksFlat.filter(m => this.guiCertificate.domains.get(this.selectedDomain) && m.level1 == mark.level1 && m.level2 == mark.level2).map(m => m.hidden));
        return unique.includes(true) ? unique.includes(false) ? null : false : true;
    }

    async loadHiddenCompetencies(): Promise<number[]> {
        return RestEndpoint.main().query({ certificateId: this.lastYearCertId }).run("api/cer/certificate/getHiddenCompetencies").listNumbers();
    }

    async copyFromLastYear(): Promise<void> {
        await this.buttonClick("copyFromLastYear", async () => {
            if (!await this.app.dialog.yesNo("Möchten Sie die ausgewählten Kompetenzen für dieses Zeugnis aus dem vorherigen Schuljahr übernehmen? Eine eventuell bereits getätige Auswahl wird überschrieben", "Übernahme")) {
                return;
            }
            let hiddenCompetencies = await this.loadHiddenCompetencies();
            for (let domain of this.guiCertificate.domains.values()) {
                for (let mark of domain.marksFlat) {
                    mark.hidden = mark.competencyId > 0 && hiddenCompetencies.includes(mark.competencyId);
                }
            }
        });
    }

    somethingChanged(): void {
        let c = this.getUpdateCertificate();
        this.addTeacherNameForDomains = Arrays.fromIt(this.guiCertificate.domains.keys()).filter(d => (c.id == 0 && c.marks.some(m => m.domain == d && m.mark > 0) ||
                    c.id > 0 && c.marks.some(m => m.domain == d)) && Utils.isNoe(this.guiCertificate.domains.get(d)?.teacher));
    }
}
